import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState } from 'react';
import { getDetallePracticas } from '../../services/PrescripcionElectronica/PrescripcionElectronica';

const getEstadoCalculado = (prescripcion) => {
  const estado =
    prescripcion.estadoCalculado ||
    prescripcion.prescripcionElectronica?.estadoCalculado ||
    null; 
  return estado;
};

const getEstadoInfo = (estadoCalculado) => {
  const codigo = String(estadoCalculado?.codigo || '').trim();
  const descripcion = String(
    estadoCalculado?.descripcion || estadoCalculado?.observacion || '',
  ).trim();

  // Mapeo de códigos: backend envía códigos numéricos en string
  // 01 = Habilitada, 02 = Vencida, 03 = Pendiente de auditoria,
  // 04 = Orden generada, 05 = Rechazada, 06 = Fuera de convenio, 07 = Anulada,
  // 08 = Habilitada parcialmente usada
  switch (codigo) {
    case '01':
    case 'HABILITADA':
      return {
        label: descripcion || 'Habilitada',
        color: 'success',
        seleccionable: true,
      };
    case '02':
    case 'VENCIDA':
      return {
        label: descripcion || 'Vencida',
        color: 'error',
        seleccionable: false,
      };
    case '03':
    case 'PENDIENTE_DE_AUDITORIA':
      return {
        label: descripcion || 'Pendiente de auditoria',
        color: 'warning',
        seleccionable: false,
      };
    case '04':
    case 'ORDEN_GENERADA':
      return {
        label: descripcion || 'Orden generada',
        color: 'default',
        seleccionable: false,
      };
    case '05':
    case 'RECHAZADA':
      return {
        label: descripcion || 'Rechazada',
        color: 'error',
        seleccionable: false,
      };
    case '06':
    case 'FUERA_DE_CONVENIO':
      return {
        label: descripcion || 'Fuera de convenio',
        color: 'warning',
        seleccionable: false,
      };
    case '07':
    case 'ANULADA':
      return {
        label: descripcion || 'Anulada',
        color: 'default',
        seleccionable: false,
      };
    case '08':
    case 'HABILITADA_PARCIALMENTE_USADA':
      return {
        label: descripcion || 'Habilitada parcialmente usada',
        color: 'success',
        seleccionable: true,
      };
    default:
      return {
        label:
          descripcion || codigo || 'Estado no disponible',
        color: 'default',
        seleccionable: false,
      };
  }
};

const getColorStyle = (color) => {
  const colorMap = {
    success: { backgroundColor: '#c8e6c9', color: '#2e7d32', borderColor: '#2e7d32' },
    error: { backgroundColor: '#ffcdd2', color: '#c62828', borderColor: '#c62828' },
    warning: { backgroundColor: '#fff3e0', color: '#e65100', borderColor: '#e65100' },
    default: { backgroundColor: '#f5f5f5', color: '#000000', borderColor: '#bdbdbd' },
  };
  return colorMap[color] || colorMap.default;
};

const PrescripcionesVigentesTable = ({
  prescripciones = [],
  afiliado = null,
  onSeleccionar,
  loading = false,
}) => {
  // Extrae DNI del afiliado; si no existe, lo obtiene de la primera prescripción
  const getDniValue = () => {
    if (afiliado?.numeroDocumento) return afiliado.numeroDocumento;
    if (afiliado?.dni) return afiliado.dni;
    if (afiliado?.nroDocumento) return afiliado.nroDocumento;
    if (prescripciones.length > 0) {
      return prescripciones[0].personaFisicaAfiliado?.numeroDocumento;
    }
    return '';
  };

  // Extrae nombre del afiliado; si no existe, lo obtiene de la primera prescripción
  const getNombreValue = () => {
    const nombre = afiliado?.nombre || prescripciones[0]?.personaFisicaAfiliado?.nombre || '';
    const apellido = afiliado?.apellido || prescripciones[0]?.personaFisicaAfiliado?.apellido || '';
    return `${nombre} ${apellido}`.trim();
  };

  const dniValue = getDniValue();
  const nombreValue = getNombreValue();

  const [detalleDialog, setDetalleDialog] = useState({ open: false, practicas: [], loading: false, error: null });

  const abrirDetalle = async (pe) => {
    const idPrescripcionElectronica = pe.prescripcionElectronica?.idPrescripcionElectronica;
    console.log('[ojito] pe completo:', pe);
    console.log('[ojito] idPrescripcionElectronica:', idPrescripcionElectronica);
    setDetalleDialog({ open: true, practicas: [], loading: true, error: null });
    try {
      const data = await getDetallePracticas(idPrescripcionElectronica);
      console.log('[ojito] respuesta getDetallePracticas (raw):', data);
      console.log('[ojito] cantidad de prácticas recibidas:', Array.isArray(data) ? data.length : 'no es array');
      if (Array.isArray(data) && data.length > 0) {
        console.log('[ojito] descripcionPracticaPrescripta:', data[0].descripcionPracticaPrescripta);
        console.log('[ojito] descripcionPracticaAutorizada:', data[0].descripcionPracticaAutorizada);
      }
      setDetalleDialog({ open: true, practicas: data || [], loading: false, error: null });
    } catch (error) {
      console.error('[ojito] error al obtener detalle:', error);
      setDetalleDialog({ open: true, practicas: [], loading: false, error: 'No se pudo cargar el detalle de prácticas.' });
    }
  };

  const cerrarDetalle = () => setDetalleDialog({ open: false, practicas: [], loading: false, error: null });

  return (
    <Box>
      {(nombreValue || dniValue) && (
        <Typography variant='subtitle1' gutterBottom>
          Prescripciones vigentes de {nombreValue}
          {dniValue && ` (DNI ${dniValue})`}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>N° PE</TableCell>
              <TableCell sx={{ width: '25%' }}>Práctica(s)</TableCell>
              <TableCell sx={{ width: '25%' }}>Profesional</TableCell>
              <TableCell>Vigencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align='center'>Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prescripciones.map((pe) => {
              const estadoCalculado = getEstadoCalculado(pe);
              const estadoInfo = estadoCalculado
                ? getEstadoInfo(estadoCalculado)
                : { label: 'Sin estado', color: 'default', seleccionable: true };
              const puedeSeleccionar = estadoInfo.seleccionable === true;             

              return (
                <TableRow key={pe.prescripcionElectronica?.idPrescripcionElectronica} hover>
                  <TableCell>{pe.prescripcionElectronica?.idPrescripcionElectronica}</TableCell>               
                  <TableCell sx={{ maxWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {pe.practicas?.map((p, i) => (
                        <Tooltip key={i} title={p.descripcionPractica} arrow placement='top'>
                          <Chip
                            label={p.descripcionPractica}
                            size='small'
                            variant='outlined'
                            sx={{
                              maxWidth: 160,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {pe.personaFisicaProfesional?.apellido} {pe.personaFisicaProfesional?.nombre}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {pe.prescripcionElectronica?.fechaPrescripcion} -{' '}
                    {pe.prescripcionElectronica?.fechaVencimiento}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={estadoInfo.label}
                      size='small'
                      variant='outlined'
                      sx={{
                        ...getColorStyle(estadoInfo.color),
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                      <IconButton
                        size='small'
                        color='info'
                        title='Ver detalle de prácticas'
                        onClick={() => abrirDetalle(pe)}
                      >
                        <VisibilityIcon fontSize='small' />
                      </IconButton>
                      {puedeSeleccionar ? (
                        <Button
                          variant='contained'
                          color='success'
                          size='small'
                          startIcon={<CheckCircleOutlineIcon />}
                          onClick={() => onSeleccionar(pe)}
                          disabled={loading}
                        >
                          Generar
                        </Button>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {prescripciones.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align='center'>
                  <Typography variant='body2' color='text.secondary'>
                    No se encontraron prescripciones vigentes.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detalleDialog.open} onClose={cerrarDetalle} maxWidth='lg' fullWidth>
        <DialogTitle>
          Detalle de prácticas
          {detalleDialog.practicas.length > 0 && (
            <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 1 }}>
              ({detalleDialog.practicas.length} práctica{detalleDialog.practicas.length !== 1 ? 's' : ''})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {detalleDialog.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {detalleDialog.error && (
            <Alert severity='error'>{detalleDialog.error}</Alert>
          )}
          {!detalleDialog.loading && !detalleDialog.error && (
            <>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Cód. prescripto</TableCell>
                      <TableCell>Cód. autorizado</TableCell>
                      <TableCell align='center'>Prescripto</TableCell>
                      <TableCell align='center'>Autorizado</TableCell>
                      <TableCell align='center'>Consumido</TableCell>
                      <TableCell align='center'>Disponible</TableCell>
                      <TableCell>N° Órdenes</TableCell>
                      <TableCell>Solicitud cobertura</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalleDialog.practicas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align='center'>
                          <Typography variant='body2' color='text.secondary'>Sin prácticas registradas.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      detalleDialog.practicas.map((p, i) => {
                        const autorizada = p.cantidadAutorizada ?? p.cantidadPrescripta;
                        const consumida = p.cantidadConsumida ?? 0;
                        const disponible = p.cupoActual ?? (autorizada - consumida);
                        const parcialmenteAutorizada = p.cantidadAutorizada !== null && p.cantidadAutorizada > 0 && p.cantidadAutorizada < p.cantidadPrescripta;
                        const codigoCambiado = p.codigoPracticaAutorizada && p.codigoPracticaAutorizada !== p.codigoPractica;
                        const ordenes = p.idsOrdenes ? p.idsOrdenes.split(',').map((s) => s.trim()).filter(Boolean) : [];
                        return (
                          <>
                            <TableRow key={p.idPracticaAutorizada ?? i} sx={{ '& td': { verticalAlign: 'middle' } }}>
                              <TableCell>
                                <Tooltip title={p.descripcionPracticaPrescripta || ''} arrow placement='top'>
                                  <Typography variant='body2' fontWeight={600} sx={{ cursor: 'help', display: 'inline' }}>
                                    {p.codigoPractica}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                {codigoCambiado ? (
                                  <Tooltip
                                    title={[p.descripcionPracticaAutorizada, 'El auditor autorizó un código diferente al prescripto'].filter(Boolean).join(' — ')}
                                    arrow
                                    placement='top'
                                  >
                                    <Typography variant='body2' fontWeight={600} color='warning.main' sx={{ cursor: 'help', display: 'inline' }}>
                                      {p.codigoPracticaAutorizada} ⚠
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={p.descripcionPracticaAutorizada || ''} arrow placement='top'>
                                    <Typography variant='body2' color='text.secondary' sx={{ cursor: 'help', display: 'inline' }}>
                                      {p.codigoPracticaAutorizada ?? '—'}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell align='center'>{p.cantidadPrescripta}</TableCell>
                              <TableCell align='center'>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {p.cantidadAutorizada ?? '-'}
                                  {parcialmenteAutorizada && (
                                    <Tooltip title='Autorización parcial' arrow>
                                      <Typography component='span' variant='caption' color='warning.main' sx={{ cursor: 'default' }}>⚠</Typography>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align='center'>{p.cantidadConsumida ?? '-'}</TableCell>
                              <TableCell align='center'>
                                <Chip
                                  label={disponible}
                                  size='small'
                                  sx={disponible > 0
                                    ? { bgcolor: '#c8e6c9', color: '#2e7d32', fontWeight: 700 }
                                    : { bgcolor: '#f5f5f5', color: '#757575', fontWeight: 700 }
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {ordenes.length > 0
                                    ? ordenes.map((ord) => (
                                        <Chip key={ord} label={ord} size='small' variant='outlined' />
                                      ))
                                    : <Typography variant='caption' color='text.secondary'>—</Typography>
                                  }
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant='body2'>{p.idSolicitudCobertura ?? '—'}</Typography>
                              </TableCell>
                            </TableRow>
                            {p.observacion && (
                              <TableRow key={`obs-${p.idPracticaAutorizada ?? i}`}>
                                <TableCell colSpan={8} sx={{ py: 0.5, bgcolor: 'grey.50' }}>
                                  <Typography variant='caption' color='text.secondary'>
                                    Obs: {p.observacion}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDetalle}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrescripcionesVigentesTable;
